import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileText, Printer } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { CompactTable } from '../../components/ui/CompactTable';
import { TableFilterBar } from '../../components/table-filters/TableFilterBar';
import { api } from '../../services/api';
import { legacyApi, Customer } from '../../services/legacyApi';
import { CustomerStatementDetail, CustomerStatementSummaryRow } from '../../services/types';
import { toast } from 'sonner';

const formatMoney = (value: number) =>
    (Number(value || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function CustomerStatement() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [summaryRows, setSummaryRows] = useState<CustomerStatementSummaryRow[]>([]);
    const [summaryTotal, setSummaryTotal] = useState(0);
    const [grandOutstanding, setGrandOutstanding] = useState(0);
    const [grandNet, setGrandNet] = useState(0);
    const [grandUnappliedAdvances, setGrandUnappliedAdvances] = useState(0);
    const [grandNetCustomerPosition, setGrandNetCustomerPosition] = useState(0);
    const [loadingSummary, setLoadingSummary] = useState(false);
    const [detail, setDetail] = useState<CustomerStatementDetail | null>(null);
    const [loadingDetail, setLoadingDetail] = useState(false);

    const q = searchParams.get('q') || '';
    const fromDate = searchParams.get('from') || '';
    const toDate = searchParams.get('to') || '';
    const selectedCustomerId = Number(searchParams.get('customer_id') || 0) || null;

    const setParam = (key: string, value?: string) => {
        const next = new URLSearchParams(searchParams);
        if (value && value.trim().length > 0) {
            next.set(key, value);
        } else {
            next.delete(key);
        }
        setSearchParams(next, { replace: true });
    };

    useEffect(() => {
        legacyApi.customers.list().then(setCustomers).catch(() => setCustomers([]));
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoadingSummary(true);
        api.financials
            .listCustomerStatementsSummary({
                q: q || undefined,
                from: fromDate || undefined,
                to: toDate || undefined,
                limit: 200,
                offset: 0,
            })
            .then((payload) => {
                if (cancelled) return;
                setSummaryRows(payload.items || []);
                setSummaryTotal(Number(payload.total || 0));
                setGrandOutstanding(Number(payload.grand_total_outstanding || 0));
                setGrandNet(Number(payload.grand_total_net || 0));
                setGrandUnappliedAdvances(Number(payload.grand_total_unapplied_advances || 0));
                setGrandNetCustomerPosition(Number(payload.grand_total_net_customer_position || 0));
            })
            .catch((err) => {
                if (cancelled) return;
                setSummaryRows([]);
                setSummaryTotal(0);
                setGrandOutstanding(0);
                setGrandNet(0);
                setGrandUnappliedAdvances(0);
                setGrandNetCustomerPosition(0);
                toast.error(err?.message || 'Failed to load customer statement summary');
            })
            .finally(() => {
                if (!cancelled) setLoadingSummary(false);
            });
        return () => {
            cancelled = true;
        };
    }, [q, fromDate, toDate]);

    useEffect(() => {
        if (!selectedCustomerId) {
            setDetail(null);
            return;
        }
        let cancelled = false;
        setLoadingDetail(true);
        api.financials
            .getCustomerStatement(selectedCustomerId, {
                from: fromDate || undefined,
                to: toDate || undefined,
            })
            .then((payload) => {
                if (!cancelled) setDetail(payload);
            })
            .catch((err) => {
                if (!cancelled) {
                    setDetail(null);
                    toast.error(err?.message || 'Failed to load customer statement');
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingDetail(false);
            });

        return () => {
            cancelled = true;
        };
    }, [selectedCustomerId, fromDate, toDate]);

    const detailRows = useMemo(
        () =>
            (detail?.transactions || []).map((row, idx) => ({
                ...row,
                row_key: `${row.source_type}-${row.source_id}-${idx}`,
            })),
        [detail],
    );

    const summaryColumns = useMemo(
        () => [
            { header: 'Customer', accessor: (r: CustomerStatementSummaryRow) => r.customer_name, className: 'font-medium' },
            { header: 'Statement Opening', accessor: (r: CustomerStatementSummaryRow) => formatMoney(r.statement_opening_balance), className: 'text-right' },
            { header: 'Statement Debits', accessor: (r: CustomerStatementSummaryRow) => formatMoney(r.statement_debits), className: 'text-right' },
            { header: 'Statement Credits', accessor: (r: CustomerStatementSummaryRow) => formatMoney(r.statement_credits), className: 'text-right' },
            { header: 'Statement Closing', accessor: (r: CustomerStatementSummaryRow) => formatMoney(r.statement_closing_balance), className: 'text-right font-semibold' },
            { header: 'AR Closing', accessor: (r: CustomerStatementSummaryRow) => formatMoney(r.closing_balance), className: 'text-right font-semibold' },
            { header: 'Unapplied Advance', accessor: (r: CustomerStatementSummaryRow) => formatMoney(r.unapplied_advances), className: 'text-right text-amber-700' },
            { header: 'Last Txn', accessor: (r: CustomerStatementSummaryRow) => r.last_txn_date || '-', className: 'whitespace-nowrap' },
            {
                header: 'Action',
                accessor: (r: CustomerStatementSummaryRow) => (
                    <Button variant="outline" size="sm" onClick={() => setParam('customer_id', String(r.customer_id))}>
                        View
                    </Button>
                ),
                className: 'text-right',
            },
        ],
        [searchParams],
    );

    const detailColumns = useMemo(
        () => [
            { header: 'Date', accessor: (r: any) => r.txn_date || '-', className: 'whitespace-nowrap' },
            { header: 'Type', accessor: (r: any) => r.doc_type, className: 'whitespace-nowrap' },
            { header: 'Doc No', accessor: (r: any) => r.doc_no, className: 'font-mono whitespace-nowrap' },
            { header: 'LPO Ref', accessor: (r: any) => r.lpo_reference || '-', className: 'whitespace-nowrap' },
            { header: 'Description', accessor: (r: any) => r.description, className: '' },
            { header: 'Debit', accessor: (r: any) => formatMoney(r.statement_debit), className: 'text-right' },
            { header: 'Credit', accessor: (r: any) => formatMoney(r.statement_credit), className: 'text-right' },
            { header: 'Statement Balance', accessor: (r: any) => formatMoney(r.statement_balance), className: 'text-right font-semibold' },
            {
                header: 'AR Impact',
                accessor: (r: any) => `Dr ${formatMoney(r.debit)} / Cr ${formatMoney(r.credit)}`,
                className: 'text-right whitespace-nowrap text-muted-foreground',
            },
            { header: 'Advance Balance', accessor: (r: any) => formatMoney(r.unapplied_advance_balance), className: 'text-right font-semibold text-amber-700' },
        ],
        [],
    );

    const selectedCustomerName = detail?.customer?.customer_name || customers.find((c) => c.customer_id === selectedCustomerId)?.customer_name || '';

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold">Customer Statement of Accounts</h2>
                    <p className="text-sm text-muted-foreground">Customer statement with advances included as credits and separately reconciled to AR.</p>
                    <p className="text-xs text-blue-700">Draft invoices are excluded from Customer SOA until posted.</p>
                </div>
                <div className="text-xs text-muted-foreground">Currency: AED</div>
            </div>

            <TableFilterBar
                searchValue={q}
                onSearchChange={(value) => setParam('q', value)}
                searchPlaceholder="Search customer name or ID..."
                isDirty={Boolean(q || fromDate || toDate || selectedCustomerId)}
                onClear={() => {
                    const next = new URLSearchParams(searchParams);
                    next.delete('q');
                    next.delete('from');
                    next.delete('to');
                    next.delete('customer_id');
                    setSearchParams(next, { replace: true });
                }}
                rightSlot={`${summaryTotal} customers`}
            />

            <div className="grid grid-cols-1 gap-3 rounded-lg border bg-card p-3 lg:grid-cols-5">
                <div className="lg:col-span-2">
                    <label className="mb-1 block text-xs font-medium text-muted-foreground">Customer</label>
                    <select
                        className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                        value={selectedCustomerId || ''}
                        onChange={(e) => setParam('customer_id', e.target.value || undefined)}
                    >
                        <option value="">Select Customer</option>
                        {customers.map((customer) => (
                            <option key={customer.customer_id} value={customer.customer_id}>
                                {customer.customer_name}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="mb-1 block text-xs font-medium text-muted-foreground">From</label>
                    <input
                        type="date"
                        className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                        value={fromDate}
                        onChange={(e) => setParam('from', e.target.value || undefined)}
                    />
                </div>
                <div>
                    <label className="mb-1 block text-xs font-medium text-muted-foreground">To</label>
                    <input
                        type="date"
                        className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                        value={toDate}
                        onChange={(e) => setParam('to', e.target.value || undefined)}
                    />
                </div>
                <div className="flex flex-wrap items-end justify-end gap-2">
                    <Button
                        variant="outline"
                        onClick={() => {
                            try {
                                api.financials.openCustomerOutstandingPrint({
                                    q: q || undefined,
                                    from: fromDate || undefined,
                                    to: toDate || undefined,
                                });
                            } catch (err: any) {
                                toast.error(err?.message || 'Failed to open outstanding report print');
                            }
                        }}
                    >
                        <Printer className="mr-2 h-4 w-4" />
                        Outstanding
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => {
                            if (!selectedCustomerId) {
                                toast.error('Select a customer to open statement print');
                                return;
                            }
                            try {
                                api.financials.openCustomerStatementPrint(selectedCustomerId, {
                                    from: fromDate || undefined,
                                    to: toDate || undefined,
                                });
                            } catch (err: any) {
                                toast.error(err?.message || 'Failed to open statement print');
                            }
                        }}
                    >
                        <Printer className="mr-2 h-4 w-4" />
                        Statement
                    </Button>
                </div>
            </div>

            <div className="rounded-lg border bg-card p-2">
                <div className="mb-2 flex items-center gap-2 px-1 text-sm font-medium">
                    <FileText className="h-4 w-4" />
                    Customer Balances
                </div>
                {loadingSummary ? (
                    <div className="p-4 text-sm text-muted-foreground">Loading customer balances...</div>
                ) : (
                    <>
                        <CompactTable data={summaryRows} columns={summaryColumns} keyField="customer_id" />
                        <div className="mt-2 flex flex-wrap items-center justify-end gap-4 rounded border bg-muted/30 px-3 py-2 text-sm">
                            <div>
                                <span className="text-muted-foreground">Grand AR Outstanding: </span>
                                <span className="font-semibold">{formatMoney(grandOutstanding)}</span>
                            </div>
                            <div>
                                <span className="text-muted-foreground">Grand AR Closing: </span>
                                <span className="font-semibold">{formatMoney(grandNet)}</span>
                            </div>
                            <div>
                                <span className="text-muted-foreground">Unapplied Advances: </span>
                                <span className="font-semibold text-amber-700">{formatMoney(grandUnappliedAdvances)}</span>
                            </div>
                            <div>
                                <span className="text-muted-foreground">Net Customer Position: </span>
                                <span className="font-semibold">{formatMoney(grandNetCustomerPosition)}</span>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {selectedCustomerId && (
                <div className="space-y-3 rounded-lg border bg-card p-3">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold">Statement Detail{selectedCustomerName ? `: ${selectedCustomerName}` : ''}</h3>
                        <div className="text-xs text-muted-foreground">
                            Period: {fromDate || 'Beginning'} to {toDate || 'Today'}
                        </div>
                    </div>

                    {loadingDetail ? (
                        <div className="p-4 text-sm text-muted-foreground">Loading statement detail...</div>
                    ) : detail ? (
                        <>
                            <div className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
                                Statement Credit includes customer advances when received. Applying an advance later reclassifies it to AR without crediting the customer twice. Statement Closing = AR Closing − Unapplied Advances.
                            </div>
                            <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
                                <div className="rounded border p-3">
                                    <div className="text-xs text-muted-foreground">Statement Opening</div>
                                    <div className="text-lg font-semibold">{formatMoney(detail.opening_statement_balance)}</div>
                                </div>
                                <div className="rounded border p-3">
                                    <div className="text-xs text-muted-foreground">Debits</div>
                                    <div className="text-lg font-semibold text-emerald-700">{formatMoney(detail.statement_debits_total)}</div>
                                </div>
                                <div className="rounded border p-3">
                                    <div className="text-xs text-muted-foreground">Credits</div>
                                    <div className="text-lg font-semibold text-blue-700">{formatMoney(detail.statement_credits_total)}</div>
                                </div>
                                <div className="rounded border p-3">
                                    <div className="text-xs text-muted-foreground">AR Closing</div>
                                    <div className="text-lg font-semibold">{formatMoney(detail.closing_balance)}</div>
                                </div>
                                <div className="rounded border border-amber-200 bg-amber-50 p-3">
                                    <div className="text-xs text-amber-800">Unapplied Advances</div>
                                    <div className="text-lg font-semibold text-amber-800">{formatMoney(detail.unapplied_advances_total)}</div>
                                </div>
                                <div className="rounded border p-3">
                                    <div className="text-xs text-muted-foreground">Statement Closing</div>
                                    <div className="text-lg font-semibold">{formatMoney(detail.closing_statement_balance)}</div>
                                </div>
                            </div>

                            <CompactTable data={detailRows} columns={detailColumns} keyField="row_key" />
                        </>
                    ) : (
                        <div className="p-4 text-sm text-muted-foreground">No statement data available for this customer.</div>
                    )}
                </div>
            )}
        </div>
    );
}
